<script setup>
/**
 * FormControlContentContentType
 * Dynamically injects fields and data into the parent form based on the
 * selected Content Type. It acts as a bridge between core content and
 * specific component data.
 */
import {ref, computed, onMounted} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useFieldsetsStore} from '@/stores/fieldsets'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'
import {useFormStore} from '@/stores/form'
import api from '@/services/api'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number],
        default: null
    }
})

// Pinia Stores
const componentsStore = useComponentsStore()
const fieldsetsStore = useFieldsetsStore()
const componentStructureStore = useComponentStructureStore()
const itemDataStore = useItemDataStore()
const formStore = useFormStore(props.formId)

// --- State ---

const contentComponentsItemId = ref(0)

// --- Computed ---

const componentIds = computed(() => componentsStore.componentIds)
const tableName = computed(() => formStore.tableName)
const recordId = computed(() => formStore.id)
const parentComponentId = computed(() => componentsStore.componentIds[tableName.value])

// --- Methods ---

const setFormData = async (componentTableName) => {
    try {
        const response = await api.get(`api/component/${componentTableName}`, {
            params: {contentId: recordId.value}
        })

        if (response.data.items?.length) {
            const item = response.data.items[0]

            // Retain the ID for M2M relationship sourcing
            contentComponentsItemId.value = item.id

            // Clean the object of system/meta fields before presetting data
            const cleanItem = {...item}
            const keysToDelete = ['id', 'contentId', `${componentTableName}ID`]
            keysToDelete.forEach(key => delete cleanItem[key])

            Object.keys(cleanItem).forEach((prop) => {
                formStore.presetData({
                    name: prop,
                    value: cleanItem[prop]
                })
            })
        }
    } catch (error) {
        console.error('Failed to set component form data:', error)
    }
}

/**
 * Fetches the structure/schema for the content type and
 * injects the fields into the form orchestrator.
 */
const setContentFormFields = async (targetComponentId) => {
    try {
        // We fetch the template to ensure it exists
        await itemDataStore.get({
            tableName: 'templates_templates',
            id: props.currentData.contentType
        })

        // Get the schema and deep clone it to avoid mutating store state
        const structure = componentStructureStore.get(targetComponentId)
        const fields = JSON.parse(JSON.stringify(structure))

        let displayOrder = 2 // Position after the title field

        fields
            .filter(f => f.columnName !== 'contentId')
            .forEach((f) => {
                f.contentComponentsItemId = contentComponentsItemId.value
                f.displayOrder = displayOrder++

                // Assign to the first available fieldset for this component
                const fieldsets = fieldsetsStore.items
                if (fieldsets.length) {
                    const firstFieldset = fieldsets.find(o => o.componentId === parentComponentId.value)
                    if (firstFieldset) f.fieldsetId = firstFieldset.id
                }

                // Prevention: Check if field already exists in the store to avoid duplicates
                const existingFields = formStore.fields || []
                const alreadyExists = existingFields.some(ef => ef.columnName === f.columnName)

                if (!alreadyExists) {
                    formStore.addField({field: f})
                }
            })
    } catch (error) {
        console.error('Failed to set content form fields:', error)
    }
}

// --- Lifecycle ---

onMounted(async () => {
    // Hide the actual control as it only serves to inject other fields
    props.field.hidden = true

    const componentTableName = `component_${props.currentData.contentType}`
    const targetComponentId = componentIds.value[componentTableName]

    if (targetComponentId) {
        // Order is important: Data must be preset before fields are rendered
        // to ensure reactive bindings catch the values.
        await setFormData(componentTableName)
        await setContentFormFields(targetComponentId)
    }
})
</script>

<template>
    <div v-if="false"/>
</template>